/**
 * Dialogs.
 *
 * Focus is trapped inside while open and restored to the opener on close;
 * Escape and a scrim click both dismiss. Rendered inline (not in a portal)
 * because the scrim is `position: fixed` and nothing in the shell creates a
 * containing block for it — one fewer moving part.
 */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from '../icons';
import { Button } from './Button';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ title, subtitle, onClose, children, footer, size = 'md' }: {
  title: string;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'narrow' | 'md' | 'wide';
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // `onClose` is almost always an inline arrow at the call site, so its identity
  // changes on every parent render. Keeping it in a ref lets the effects below
  // run ONCE on mount: an effect that depended on `onClose` would re-run after
  // every keystroke in a controlled field, and its cleanup would drag focus back
  // out of the input the user was typing into.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Mount-only: remember the opener, move focus into the dialog, freeze the page
  // behind it, and undo all three on unmount.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const first = dialog?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dialog)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  // Escape closes; Tab is trapped inside the dialog.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="scrim" onClick={onClose}>
      <div
        className={cx('modal', size !== 'md' && size)}
        ref={dialogRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="card-head">
          <div className="stack grow">
            <span className="card-title">{title}</span>
            {subtitle && <span className="card-sub">{subtitle}</span>}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon.Close aria-hidden />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * The one destructive-confirmation dialog for the whole app, so "are you sure"
 * never gets re-invented — or skipped — per page.
 */
export function ConfirmDialog({ title, body, confirmLabel = 'Delete', tone = 'danger', busy, onConfirm, onCancel }: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="t-sm" style={{ lineHeight: 'var(--lh-body)' }}>{body}</div>
    </Modal>
  );
}
